import { IForm, IFormParams } from 'pages/forms/types';
import { useCallback, useState } from 'react';
import { useApi } from 'services/api';
import config from 'controllers/config';
import { AxiosInstance } from 'axios';
import { controlledFetch } from 'utils/http';
import { useSnackbar } from 'hooks';

async function getFormsAction(api: AxiosInstance, params: IFormParams) {
  const backendUrl = config.API_NODE;

  if (backendUrl) {
    const headers = { userfunctionkey: 'c80a0ae7cdf09e28c72e949ee37df341' };
    const { data } = await api.get<IForm[]>(`${backendUrl}/forms`, { headers, params });
    return data;
  }
}

const controlledGetFormsAction = controlledFetch(getFormsAction);

export const useFormFetcher = () => {
  const { api } = useApi();
  const [data, setData] = useState<IForm[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isDataError, setIsDataError] = useState(false);
  const { Snackbar, toggleSnackbar } = useSnackbar();

  const getForms = useCallback(async (params: IFormParams) => controlledGetFormsAction(api, params), []);

  const loadForms = useCallback(
    async (params: IFormParams) => {
      setIsDataError(false);
      setIsDataLoading(true);
      setData([]);
      getForms(params)
        .then((data) => {
          if (data) {
            setData(data);
          }
        })
        .catch(() => {
          setIsDataError(true);
          setIsDataLoading(false);
        })
        .finally(() => {
          setIsDataLoading(false);
        });
    },
    [getForms]
  );

  const [isLoadingCancelForm, setIsLoadingCancelForm] = useState(false);
  const cancelForm = useCallback(
    async ({ id }: { id: number }) => {
      const backendUrl = config.API_NODE;

      if (backendUrl) {
        const headers = { userfunctionkey: 'c80a0ae7cdf09e28c72e949ee37df341' };
        return await api.put(`${backendUrl}/forms/cancel/${id}`, { headers });
      }
    },
    [api]
  );

  const loadCancelForm = useCallback(
    async ({ id }: { id: number }) => {
      if (!id) return;

      setIsLoadingCancelForm(true);
      await cancelForm({ id })
        .catch((error) => {
          const defaultMessage = 'Ocorreu um erro ao tentar cancelar o formulário!';
          toggleSnackbar({ message: error.response?.data?.message || defaultMessage, type: 'error', duration: 1500 });
        })
        .finally(() => setIsLoadingCancelForm(false));
    },
    [cancelForm, toggleSnackbar]
  );

  const [isLoadingGetSuppliersAnswers, setIsLoadingGetSuppliersAnswers] = useState(false);
  const getSuppliersAnswers = useCallback(
    async (id: number) => {
      const backendUrl = config.API_NODE;

      if (backendUrl) {
        const headers = { userfunctionkey: 'c80a0ae7cdf09e28c72e949ee37df341' };
        return (await api.get(`${backendUrl}/forms/answers/${id}`, { headers })).data;
      }
    },
    [api]
  );

  const loadGetSuppliersAnswers = useCallback(
    async (id: number) => {
      if (!id) return;
      setIsLoadingGetSuppliersAnswers(true);
      return await getSuppliersAnswers(id)
        .catch((error) => {
          const defaultMessage = 'Ocorreu um erro ao tentar buscar respostas do formulário!';
          toggleSnackbar({ message: error.response?.data?.message || defaultMessage, type: 'error', duration: 1500 });
        })
        .finally(() => setIsLoadingGetSuppliersAnswers(false));
    },
    [getSuppliersAnswers, toggleSnackbar]
  );

  return {
    data,
    isDataLoading,
    isDataError,
    getForms,
    loadForms,
    loadCancelForm,
    setIsLoadingCancelForm,
    isLoadingCancelForm,
    Snackbar,
    isLoadingGetSuppliersAnswers,
    loadGetSuppliersAnswers
  };
};
